import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Modal } from 'flowbite';

import AthletesTable from './AthletesTable';
import SportAthletesCreateModal from './SportAthletesCreateModal';

import { modalOptions } from 'Helpers/modalOptions';

import Button from 'CommonComponents/Button';
import ConfirmationModal from 'CommonComponents/ConfirmationModal';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';

import PlusCircleIcon from 'Assets/icons/plus-circle.svg';
import SearchIcon from 'Assets/icons/search.svg';

export default function SportAthletesIndexTable(props) {
    const [athletes, setAthletes] = useState([]);
    const [nationalities, setNationalities] = useState([]);

    // Pagination Variables
    const [perPage] = useState(10);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [to, setTo] = useState(1);
    const [from, setFrom] = useState(1);
    const [total, setTotal] = useState(1);

    const [searchValue, setSearchValue] = useState('');

    const [isLoading, setIsLoading] = useState(1);
    const [error, setError] = useState('');

    const [deletedAthlete, setDeletedAthlete] = useState({});

    const [actionCompleted, setActionCompleted] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [actionFailed, setActionFailed] = useState(false);

    const [sportAthletesCreateModalElement, setSportAthletesCreateModalElement] = useState(null);
    const [sportAthletesDeleteModalElement, setSportAthletesDeleteModalElement] = useState(null);

    const sportAthletesCreateModalRef = useRef();
    const sportAthletesDeleteModalRef = useRef();

    useEffect(() => {
        fetchAthletes();
        fetchNationalities();

        sportAthletesCreateModalRef && setSportAthletesCreateModalElement(new Modal(sportAthletesCreateModalRef.current, modalOptions(false)));
        sportAthletesDeleteModalRef && setSportAthletesDeleteModalElement(new Modal(sportAthletesDeleteModalRef.current, modalOptions(false)));
    }, []);

    useEffect(() => {
        if (Object.keys(deletedAthlete).length > 0) {
            showDeleteForm();
        }
    }, [deletedAthlete]);

    const defaultUrl = props.sportAthletesIndexUrl + '?per_page=' + perPage;

    const combineFetchUrl = (p = null, pp = null) => {
        return props.sportAthletesIndexUrl + '?per_page=' + (pp || perPage) + '&page=' + (p || page) + (searchValue.length > 0 ? '&search=' + searchValue : '');
    };

    const fetchAthletes = (url = null) => {
        setIsLoading(true);
        setError('');
        const fetchUrl = url || defaultUrl;

        axios
            .get(fetchUrl)
            .then((res) => {
                const { data, to, from, total, last_page } = res.data;
                setAthletes(data);
                setTo(to);
                setFrom(from);
                setTotal(total);
                setLastPage(last_page);
                setIsLoading(false);
            })
            .catch((error) => {
                setAthletes([]);
                setIsLoading(false);
                setError('There was an error fetching athletes');
            });
    };

    const fetchNationalities = () => {
        setError('');

        axios
            .get(props.sportAthletesCreateUrl)
            .then((response) => {
                const nationalitiesObjects = response.data.nationalities.map((nationality) => {
                    return { value: nationality.nationality, label: nationality.nationality };
                });
                setNationalities([{ value: '', label: $t('Undisclosed') }, ...nationalitiesObjects]);
            })
            .catch((e) => {
                setError('There was an error fetching Nationalities');
            });
    };

    function pageChange(data) {
        const url = combineFetchUrl(data.selected + 1);
        setPage(+data.selected + 1);
        fetchAthletes(url);
    }

    const handleChange = (event) => {
        setSearchValue(event.target.value);
    };

    const handleKeyPress = (event) => {
        if (event.key == 'Enter') {
            const url = combineFetchUrl(1);
            setPage(1);
            fetchAthletes(url);
        }
    };

    const showModalElemenet = (modalElement) => {
        modalElement && modalElement.show();
    };

    const hideModalElemenet = (modalElement) => {
        modalElement && modalElement.hide();
    };

    const showCreateForm = () => {
        showModalElemenet(sportAthletesCreateModalElement);
    };

    const showDeleteForm = () => {
        showModalElemenet(sportAthletesDeleteModalElement);
    };

    const successHandlerCreate = () => {
        if (page === lastPage && athletes.length < perPage) {
            setAthletes([]);
            const url = combineFetchUrl(page);
            fetchAthletes(url);
        } else if (page === lastPage && athletes.length === perPage) {
            setLastPage(lastPage + 1);
        }
        hideModalElemenet(sportAthletesCreateModalElement);
    };

    const successHandlerDelete = () => {
        const url = combineFetchUrl(page !== lastPage || athletes.length > 1 ? page : page - 1);
        fetchAthletes(url);
        hideModalElemenet(sportAthletesDeleteModalElement);
    };

    const successHandlerUpdate = () => {
        const url = combineFetchUrl(page);
        fetchAthletes(url);

        hideModalElemenet(sportAthletesDeleteModalElement);
    };

    const closeModal = () => {
        hideModalElemenet(sportAthletesCreateModalElement);
        hideModalElemenet(sportAthletesDeleteModalElement);
    };

    const cleanConfirmationModalStates = useCallback(() => {
        setActionInProgress(false);
        setActionFailed(false);
        setActionCompleted(false);
    }, [actionInProgress, actionFailed, actionCompleted]);

    const triggerDeleteAthlete = () => {
        const doDelete = () => {
            setActionInProgress(true);

            axios
                .delete(sportAthletesUpdateUrl.replace('ATHLETE_ID', deletedAthlete?.id))
                .then(() => {
                    setActionCompleted(true);
                    if (window.quantityLinkEnabled) {
                        window.currentSportAthleteCount = Math.max(0, (window.currentSportAthleteCount || 0) - 1);
                        window.currentTotalAthleteCount = Math.max(0, (window.currentTotalAthleteCount || 0) - 1);
                    }
                })
                .catch(() => {
                    setActionFailed(true);
                });
        };

        if (window.quantityLinkEnabled && window.quantityLinkDriverImpactPreviewUrl) {
            const baseUrl = window.quantityLinkDriverImpactPreviewUrl;

            const sportParams = new URLSearchParams({
                source_type: 'App\\Sport',
                source_id: String(window.sportSourceId),
            });

            const eventParams = new URLSearchParams({
                source_type: 'App\\Event',
                source_id: String(window.eventSourceId),
                driver: 'athlete_count',
            });

            const checkEventLevel = () => {
                window.dispatchEvent(
                    new CustomEvent('quantityimpact:check', {
                        detail: {
                            previewUrl: baseUrl + '?' + eventParams.toString(),
                            newValue: Math.max(0, (window.currentTotalAthleteCount || 0) - 1),
                            formEl: null,
                            onConfirm: doDelete,
                        },
                    }),
                );
            };

            window.dispatchEvent(
                new CustomEvent('quantityimpact:check', {
                    detail: {
                        previewUrl: baseUrl + '?' + sportParams.toString(),
                        newValue: Math.max(0, (window.currentSportAthleteCount || 0) - 1),
                        formEl: null,
                        onConfirm: checkEventLevel,
                    },
                }),
            );
        } else {
            doDelete();
        }
    };

    return (
        <>
            {error !== '' && (
                <div className="alert alert-danger">
                    <ul>
                        <li>{error}</li>
                    </ul>
                </div>
            )}
            <div className="row d-flex justify-content-end">
                <div className="input-group m-3 col-2">
                    <div className="input-group-prepend">
                        <span className="input-group-text px-3">
                            <SearchIcon className="tw-w-2.5 tw-h-2.5" />
                        </span>
                    </div>
                    <input type="text" className="form-control" value={searchValue} onChange={handleChange} onKeyPress={handleKeyPress}></input>
                </div>
                <div className="m-3">
                    <Button as="a" variant="primary" className="mr-3" onClick={showCreateForm}>
                        <PlusCircleIcon className="tw-w-3.5 tw-h-3.5 tw-mr-2" />
                        <span className="pl-1">{$t('Add new athlete')}</span>
                    </Button>
                </div>
            </div>
            <div className="row d-block">
                <AthletesTable
                    athletes={athletes}
                    nationalities={nationalities}
                    loading={isLoading}
                    sportAthletesUpdateUrl={sportAthletesUpdateUrl}
                    pageUseTopIndex={Math.ceil((to + from) / 2)}
                    page={(page - 1) * perPage}
                    onUpdated={() => successHandlerUpdate()}
                    onDelete={(athlete) => setDeletedAthlete(athlete)}
                    onError={(e) => setError(e)}
                />
                <div className={'d-flex justify-content-between align-items-center py-3'}>
                    <PaginationInfo to={to} from={from} total={total} entries={$t('Athletes')} />
                    <TablePagination lastPage={lastPage} pageChange={pageChange} />
                </div>
                <SportAthletesCreateModal
                    closeHandler={closeModal}
                    successHandler={successHandlerCreate}
                    modalId={'create-sport-athlete'}
                    modalRef={sportAthletesCreateModalRef}
                    modalElement={sportAthletesCreateModalElement}
                    nationalities={nationalities}
                    sportAthletesStoreUrl={props.sportAthletesStoreUrl}
                />
                <ConfirmationModal
                    closeHandler={closeModal}
                    successHandler={successHandlerDelete}
                    triggerAction={triggerDeleteAthlete}
                    cleanStates={cleanConfirmationModalStates}
                    allowAction={Object.keys(deletedAthlete).length > 0}
                    modalId={'delete-sport-athlete'}
                    modalRef={sportAthletesDeleteModalRef}
                    modalElement={sportAthletesDeleteModalElement}
                    request={actionInProgress}
                    actionCompleted={actionCompleted}
                    actionFailed={actionFailed}
                    confirmationMsg={$t('Are you sure you want to delete {{name}}?', { name: `${deletedAthlete?.first_name} ${deletedAthlete?.last_name}` })}
                    actionCompletedMsg={$t('{{name}} deleted', { name: `${deletedAthlete?.first_name} ${deletedAthlete?.last_name}` })}
                    actionFailedMsg={$t('{{name}} could not be deleted', { name: `${deletedAthlete?.first_name} ${deletedAthlete?.last_name}` })}
                    title={$t('Delete Athlete')}
                    cancelButtonText="Cancel"
                    primaryButtonText="Delete"
                />
            </div>
        </>
    );
}
